import React, { useState, useRef, useMemo } from "react";
import { db } from "../config/firebase";
import {
  collection,
  doc,
  setDoc,
  addDoc,
  Timestamp,
  getDocs,
  writeBatch,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import moment from "moment";
import { normalizeIrn } from "../utils/irn";
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Input as MuiInput,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown, faUpload } from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../utils/exportTemplate";

const EditUpload = ({
  collectionName = "TpStudents",
  onActionComplete,
  exportData,
  exportColumnOrder,
  exportColumnLabels = {},
  exportFileName,
  booleanFields = [],
  primaryKeyField = "id",
  inline = false,
  templateFields,
}) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const fileInputRef = useRef(null);

  // Reverse-map friendly export labels (e.g. "Site IRN") back to their
  // Firestore field name (e.g. "SiteID") so re-importing an exported file
  // still writes to the correct field.
  const importFieldAliases = useMemo(() => {
    const map = {};
    Object.entries(exportColumnLabels).forEach(([field, label]) => {
      const normalizedLabel = String(label)
        .replace(/ /g, "_")
        .replace(/[^a-zA-Z0-9_]/gi, "");
      if (normalizedLabel && normalizedLabel !== field) {
        map[normalizedLabel] = field;
      }
    });
    return map;
  }, [exportColumnLabels]);

  const formatDateInternal = (dateValue) => {
    if (!dateValue && typeof dateValue !== "number") return null;

    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return null;
      return Timestamp.fromDate(dateValue);
    }

    if (typeof dateValue === "number") {
      // Excel date conversion
      const excelEpoch = new Date(1900, 0, 1);
      const millisecondsInDay = 86400000;
      const adjustedDays = dateValue - 2;
      const date = new Date(
        excelEpoch.getTime() + adjustedDays * millisecondsInDay,
      );

      if (isNaN(date.getTime())) {
        console.warn(
          "Numeric value did not convert to a valid date:",
          dateValue,
        );
        return null;
      }

      const utcDate = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          12,
          0,
          0,
          0,
        ),
      );

      return Timestamp.fromDate(utcDate);
    }

    if (typeof dateValue === "string") {
      // Handle empty strings
      if (dateValue.trim() === "") return null;

      const formats = [
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "YYYY-MM-DD HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss",
        "MM-DD-YYYY",
        "DD-MM-YYYY",
        "YYYY/MM/DD",
        "M/D/YYYY",
        "M-D-YYYY",
      ];

      let formattedDate = moment(dateValue, formats, true);
      if (!formattedDate.isValid()) {
        formattedDate = moment(dateValue);
      }

      if (formattedDate.isValid()) {
        formattedDate.utc().hour(12).minute(0).second(0).millisecond(0);
        return Timestamp.fromDate(formattedDate.toDate());
      }
    }

    console.warn("Failed to parse date value:", dateValue);
    return null;
  };

  const valuesAreEqual = (newValue, existingValue) => {
    if (
      (newValue === null || newValue === undefined) &&
      (existingValue === null || existingValue === undefined)
    ) {
      return true;
    }
    if (
      newValue === null ||
      newValue === undefined ||
      existingValue === null ||
      existingValue === undefined
    ) {
      return false;
    }

    if (newValue instanceof Timestamp && existingValue instanceof Timestamp) {
      return newValue.seconds === existingValue.seconds;
    }

    if (typeof newValue === "number" && typeof existingValue === "number") {
      return newValue === existingValue;
    }

    return String(newValue) === String(existingValue);
  };

  const hasDataChanged = (newData, existingData) => {
    const allFields = new Set([
      ...Object.keys(newData).filter(
        (key) => key !== "updatedAt" && key !== "created_at",
      ),
      ...Object.keys(existingData).filter(
        (key) => key !== "updatedAt" && key !== "created_at",
      ),
    ]);

    for (const key of allFields) {
      if (!valuesAreEqual(newData[key], existingData[key])) {
        return true;
      }
    }
    return false;
  };

  const formatBooleanForExport = (value, fieldName) => {
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          item && typeof item === "object"
            ? item.SiteName || item.name || item.label || JSON.stringify(item)
            : item,
        )
        .filter(Boolean)
        .join(", ");
    }

    const isBooleanField =
      booleanFields.includes(fieldName) ||
      fieldName.toLowerCase().includes("active") ||
      fieldName.toLowerCase().includes("enabled") ||
      fieldName.toLowerCase().includes("approval") ||
      fieldName.toLowerCase().includes("option") ||
      fieldName.toLowerCase().includes("flag") ||
      fieldName.toLowerCase().includes("status") ||
      fieldName.toLowerCase().includes("visible") ||
      fieldName.toLowerCase().includes("available");

    if (isBooleanField) {
      if (collectionName === "Divisions" && fieldName === "Active") {
        if (value === true || value === 1 || value === "1") {
          return "Active";
        } else if (value === false || value === 0 || value === "0") {
          return "Inactive";
        }
      } else {
        const normalized =
          typeof value === "string" ? value.trim().toLowerCase() : value;
        if (
          value === -1 ||
          value === "-1" ||
          value === 1 ||
          value === "1" ||
          value === true ||
          normalized === "y" ||
          normalized === "yes"
        ) {
          return "Yes";
        } else if (
          value === 0 ||
          value === "0" ||
          value === false ||
          normalized === "n" ||
          normalized === "no"
        ) {
          return "No";
        }
      }
    }
    return value;
  };

  const handleExport = async () => {
    setLoading(true);
    setSnackbar({ open: false, message: "", severity: "info" });
    try {
      let dataForSheet = [];

      if (exportData && exportData.length > 0) {
        dataForSheet = exportData.map((doc) => {
          const rowData = {};
          if (doc.id) rowData.id = doc.id;

          if (exportColumnOrder && Array.isArray(exportColumnOrder)) {
            exportColumnOrder.forEach((key) => {
              if (key === "created_at" || key === "createdAt" || key === "id")
                return;
              const label = exportColumnLabels[key] || key;
              if (doc[key] instanceof Timestamp) {
                rowData[label] = doc[key]
                  .toDate()
                  .toISOString()
                  .split("T")[0];
              } else {
                rowData[label] = formatBooleanForExport(doc[key], key);
              }
            });

            Object.keys(doc).forEach((key) => {
              if (
                key === "created_at" ||
                key === "createdAt" ||
                exportColumnOrder.includes(key) ||
                key === "id"
              )
                return;
              const label = exportColumnLabels[key] || key;
              if (doc[key] instanceof Timestamp) {
                rowData[label] = doc[key]
                  .toDate()
                  .toISOString()
                  .split("T")[0];
              } else {
                rowData[label] = formatBooleanForExport(doc[key], key);
              }
            });
          } else {
            for (const key in doc) {
              if (key === "created_at" || key === "createdAt") continue;
              const label = exportColumnLabels[key] || key;
              if (doc[key] instanceof Timestamp) {
                rowData[label] = doc[key]
                  .toDate()
                  .toISOString()
                  .split("T")[0];
              } else {
                rowData[label] = formatBooleanForExport(doc[key], key);
              }
            }
          }
          return rowData;
        });
      } else {
        const querySnapshot = await getDocs(collection(db, collectionName));
        dataForSheet = querySnapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const rowData = { id: docSnapshot.id };

          if (exportColumnOrder && Array.isArray(exportColumnOrder)) {
            exportColumnOrder.forEach((key) => {
              if (key === "created_at" || key === "createdAt") return;
              const label = exportColumnLabels[key] || key;
              if (data[key] instanceof Timestamp) {
                rowData[label] = data[key]
                  .toDate()
                  .toISOString()
                  .split("T")[0];
              } else {
                rowData[label] = formatBooleanForExport(data[key], key);
              }
            });
          } else {
            for (const key in data) {
              if (key === "created_at" || key === "createdAt") continue;
              const label = exportColumnLabels[key] || key;
              if (data[key] instanceof Timestamp) {
                rowData[label] = data[key]
                  .toDate()
                  .toISOString()
                  .split("T")[0];
              } else {
                rowData[label] = formatBooleanForExport(data[key], key);
              }
            }
          }
          return rowData;
        });
      }

      if (dataForSheet.length === 0) {
        setSnackbar({
          open: true,
          message: "No data to export.",
          severity: "info",
        });
        setLoading(false);
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${exportFileName || collectionName}.xlsx`);

      setSnackbar({
        open: true,
        message: "Data exported successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      setSnackbar({
        open: true,
        message: `Error exporting data: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportTemplate = () => {
    const fields =
      templateFields && templateFields.length > 0
        ? templateFields
        : exportColumnOrder || [];
    if (fields.length === 0) {
      setSnackbar({
        open: true,
        message: "No fields configured for this template.",
        severity: "info",
      });
      return;
    }
    downloadExcelTemplate(
      fields,
      `${exportFileName || collectionName}-template`,
    );
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setSnackbar({ open: false, message: "", severity: "info" });

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fileData = new Uint8Array(e.target.result);
        const workbook = XLSX.read(fileData, {
          type: "array",
          cellDates: true,
        });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

        if (jsonData.length === 0) {
          setSnackbar({
            open: true,
            message: "The uploaded file is empty or has no data.",
            severity: "warning",
          });
          setLoading(false);
          return;
        }

        // Course Shell rows name the site/partner by their human-readable
        // name — resolve those to the stable SiteID/PartnerIRN once, up
        // front, instead of trusting a name typed into a spreadsheet to
        // stay the join key (the same rename-breaks-the-connection bug
        // fixed in CourseShell/inc/Add.js and Edit.js). Fetched once for
        // the whole upload rather than per row.
        let siteNameToIdMap = {};
        let partnerNameToIrnMap = {};
        // Normalized (leading-zero-stripped) IRN -> canonical padded IRN,
        // so a directly-typed sod_irn/tp_partner_irn column value that's
        // missing its leading zeros (e.g. "19407" instead of the real
        // "019407") still resolves to the same record a name lookup would.
        let siteIrnCanonicalMap = {};
        let partnerIrnCanonicalMap = {};
        if (collectionName === "TpCourseShell") {
          const [sitesSnap, partnersSnap] = await Promise.all([
            getDocs(collection(db, "Sites")),
            getDocs(collection(db, "TpPartners")),
          ]);
          sitesSnap.docs.forEach((d) => {
            const site = d.data();
            if (site.SiteNameShort) {
              siteNameToIdMap[site.SiteNameShort.trim()] = site.SiteID || "";
            }
            if (site.SiteID) {
              siteIrnCanonicalMap[normalizeIrn(site.SiteID)] = site.SiteID;
            }
          });
          partnersSnap.docs.forEach((d) => {
            const partner = d.data();
            if (partner.Tech_Prep_Partner) {
              partnerNameToIrnMap[partner.Tech_Prep_Partner.trim()] =
                partner.PartnerIRN || "";
            }
            if (partner.PartnerIRN) {
              partnerIrnCanonicalMap[normalizeIrn(partner.PartnerIRN)] =
                partner.PartnerIRN;
            }
          });
        }

        let currentBatch = writeBatch(db);
        let operationCount = 0;
        let updateCount = 0;
        let addCount = 0;
        let skippedCount = 0;
        let errorRows = [];

        const specificDateHeaders = [
          "application_dt",
          "dob",
          "last_updated",
          "added_to_portal",
          "created_at",
          "colleague_start_date",
          "colleague_end_date",
        ];

        for (const [rowIndex, rowFromExcel] of jsonData.entries()) {
          try {
            console.log(`Processing row ${rowIndex + 1}:`, rowFromExcel);
            const docData = {};
            let docId = null;

            for (const excelHeader in rowFromExcel) {
              if (!rowFromExcel.hasOwnProperty(excelHeader)) continue;

              const normalizedHeader = excelHeader
                .replace(/ /g, "_")
                .replace(/[^a-zA-Z0-9_]/gi, "");
              const firestoreField =
                importFieldAliases[normalizedHeader] || normalizedHeader;
              let value = rowFromExcel[excelHeader];

              console.log(
                `Field mapping: "${excelHeader}" -> "${firestoreField}" = "${value}"`,
              );

              if (
                firestoreField === primaryKeyField ||
                firestoreField === "id"
              ) {
                docId =
                  value !== null && value !== undefined
                    ? String(value).trim()
                    : null;
                if (docId === "") docId = null;
                console.log(`Found ID field (${firestoreField}): ${docId}`);
                continue;
              }

              if (
                value === "NULL" ||
                value === null ||
                value === undefined ||
                value === ""
              ) {
                docData[firestoreField] = null;
                continue;
              }

              if (
                specificDateHeaders.includes(firestoreField.toLowerCase()) ||
                firestoreField.toLowerCase().includes("date") ||
                firestoreField.toLowerCase().includes("dt")
              ) {
                const ts = formatDateInternal(value);
                docData[firestoreField] = ts;
                if (ts === null && value !== null && value !== "") {
                  console.warn(
                    `Date parsing failed for field ${firestoreField} with value:`,
                    value,
                  );
                }
              } else {
                docData[firestoreField] = String(value);
              }
            }

            console.log(
              `Row ${rowIndex + 1} - docId: ${docId}, docData:`,
              docData,
            );

            // Special handling for CTAG collection - calculate derived fields and fix data types
            if (collectionName === "TpCtagCatalog") {
              // Handle numeric fields properly
              if (
                docData.Credits !== null &&
                docData.Credits !== undefined &&
                docData.Credits !== ""
              ) {
                const creditsValue = parseFloat(docData.Credits);
                docData.Credits = isNaN(creditsValue) ? 0 : creditsValue;
              }

              // Handle boolean fields that use -1/0 values
              const booleanFields = [
                "ODHE_Portal_In",
                "Colleague_TCEQ_In",
                "OK_to_Post",
              ];
              booleanFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const stringValue = docData[field]
                    .toString()
                    .toLowerCase()
                    .trim();
                  if (
                    stringValue === "yes" ||
                    stringValue === "true" ||
                    stringValue === "1" ||
                    stringValue === "-1"
                  ) {
                    docData[field] = -1;
                  } else if (
                    stringValue === "no" ||
                    stringValue === "false" ||
                    stringValue === "0"
                  ) {
                    docData[field] = 0;
                  } else {
                    docData[field] = 0; // Default to 0 for unrecognized values
                  }
                }
              });

              // Calculate Colleague_CTAN: Left([@field:CTAN],4) + '*' + Substring([@field:CTAN],5,(Len([@field:CTAN])-4))
              if (docData.CTAN && docData.CTAN.toString().length >= 4) {
                const ctan = docData.CTAN.toString();
                const left4 = ctan.substring(0, 4);
                const remaining = ctan.substring(4);
                docData.Colleague_CTAN = `${left4}*${remaining}`;
              }

              // Calculate Sinclair_Course_and_Subject: [@field:Course]+': '+[@field:Subject]
              if (docData.Sinclair_Course && docData.Sinclair_Subject_Long) {
                docData.Sinclair_Course_and_Subject = `${docData.Sinclair_Course}: ${docData.Sinclair_Subject_Long}`;
              }
            }

            // Special handling for Course Catalog collection - fix data types for boolean fields
            if (collectionName === "TpCourseCatalog") {
              // Handle boolean fields that use -1/0 values
              const booleanFields = [
                "Lab_Type_Course",
                "Shell_Option",
                "Pilot",
                "New",
              ];
              booleanFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const stringValue = docData[field]
                    .toString()
                    .toLowerCase()
                    .trim();
                  if (
                    stringValue === "yes" ||
                    stringValue === "true" ||
                    stringValue === "1" ||
                    stringValue === "-1"
                  ) {
                    docData[field] = -1;
                  } else if (
                    stringValue === "no" ||
                    stringValue === "false" ||
                    stringValue === "0"
                  ) {
                    docData[field] = 0;
                  } else {
                    docData[field] = 0; // Default to 0 for unrecognized values
                  }
                }
              });
            }

            // Special handling for Sites collection - fix data types for boolean fields
            if (collectionName === "Sites") {
              // Handle boolean fields that use -1/0 values
              const siteBooleanFields = ["Coordinator_Site"];
              siteBooleanFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const stringValue = docData[field]
                    .toString()
                    .toLowerCase()
                    .trim();
                  if (
                    stringValue === "yes" ||
                    stringValue === "true" ||
                    stringValue === "1" ||
                    stringValue === "-1"
                  ) {
                    docData[field] = -1;
                  } else if (
                    stringValue === "no" ||
                    stringValue === "false" ||
                    stringValue === "0"
                  ) {
                    docData[field] = 0;
                  } else {
                    docData[field] = 0; // Default to 0 for unrecognized values
                  }
                }
              });
            }

            // Special handling for TpCourseShell - resolve the stable
            // SiteID/PartnerIRN from whatever site/partner name was typed
            // into the spreadsheet, same as the dropdowns in Add.js/Edit.js
            // do, so a bulk-uploaded shell's relationship survives a later
            // rename too. An explicit sod_irn/tp_partner_irn column in the
            // sheet takes precedence over the name lookup if both are present.
            if (collectionName === "TpCourseShell") {
              if (!docData.sod_irn && docData.tp_site_of_delivery) {
                const resolvedSiteId =
                  siteNameToIdMap[String(docData.tp_site_of_delivery).trim()];
                if (resolvedSiteId) {
                  docData.sod_irn = resolvedSiteId;
                }
              }
              if (!docData.tp_partner_irn && docData.tp_partner) {
                const resolvedPartnerIrn =
                  partnerNameToIrnMap[String(docData.tp_partner).trim()];
                if (resolvedPartnerIrn) {
                  docData.tp_partner_irn = resolvedPartnerIrn;
                }
              }
              // A directly-typed sod_irn/tp_partner_irn column (or one
              // just resolved by name above) might be missing its leading
              // zeros — snap it to the real, canonically-padded value so
              // it actually matches the Select in Add.js/Edit.js.
              if (docData.sod_irn) {
                const canonicalSiteId =
                  siteIrnCanonicalMap[normalizeIrn(docData.sod_irn)];
                if (canonicalSiteId) {
                  docData.sod_irn = canonicalSiteId;
                }
              }
              if (docData.tp_partner_irn) {
                const canonicalPartnerIrn =
                  partnerIrnCanonicalMap[normalizeIrn(docData.tp_partner_irn)];
                if (canonicalPartnerIrn) {
                  docData.tp_partner_irn = canonicalPartnerIrn;
                }
              }
            }

            // Special handling for TpPartners collection - normalize Active_yn to "y"/"n"
            if (collectionName === "TpPartners") {
              const partnerBooleanFields = ["Active_yn"];
              partnerBooleanFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const stringValue = docData[field]
                    .toString()
                    .toLowerCase()
                    .trim();
                  if (
                    stringValue === "y" ||
                    stringValue === "yes" ||
                    stringValue === "true" ||
                    stringValue === "1" ||
                    stringValue === "-1"
                  ) {
                    docData[field] = "y";
                  } else if (
                    stringValue === "n" ||
                    stringValue === "no" ||
                    stringValue === "false" ||
                    stringValue === "0"
                  ) {
                    docData[field] = "n";
                  } else {
                    docData[field] = "n"; // Default to "n" for unrecognized values
                  }
                }
              });
            }

            // Special handling for Divisions collection - Active is boolean true/false
            if (collectionName === "Divisions") {
              if (
                docData.Active !== null &&
                docData.Active !== undefined &&
                docData.Active !== ""
              ) {
                const stringValue = docData.Active.toString()
                  .toLowerCase()
                  .trim();
                docData.Active =
                  stringValue === "active" ||
                  stringValue === "true" ||
                  stringValue === "1" ||
                  stringValue === "yes";
              }
            }

            // Special handling for FpsCourses collection - fix data types for boolean fields and numeric fields
            if (collectionName === "FpsCourses") {
              // Handle Credits as numeric field
              if (
                docData.Credits !== null &&
                docData.Credits !== undefined &&
                docData.Credits !== ""
              ) {
                const creditsValue = parseFloat(docData.Credits);
                docData.Credits = isNaN(creditsValue) ? 0 : creditsValue;
              }

              // Handle boolean fields that use -1/0 values
              const fpsBooleanFields = ["Active"];
              fpsBooleanFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const stringValue = docData[field]
                    .toString()
                    .toLowerCase()
                    .trim();
                  if (
                    stringValue === "yes" ||
                    stringValue === "true" ||
                    stringValue === "1" ||
                    stringValue === "-1"
                  ) {
                    docData[field] = -1;
                  } else if (
                    stringValue === "no" ||
                    stringValue === "false" ||
                    stringValue === "0"
                  ) {
                    docData[field] = 0;
                  } else {
                    docData[field] = 0; // Default to 0 for unrecognized values
                  }
                }
              });
            }

            // Special handling for TpStudents collection - fix data types for boolean fields and numeric fields
            if (collectionName === "TpStudents") {
              // Handle numeric fields properly
              const numericFields = [
                "Sem7GPA",
                "Sem8GPA",
                "final_gpa",
                "SCCID",
                "HomeSchoolID",
                "SSID",
                "PhoneNumber",
                "Last_Four_SSN",
                "ProgramDeliveryID",
                "DeliveryDetailID",
                "Secondary_Delivery_ID",
                "SC_Institution_Id_Nb",
                "reach_contact_id",
              ];

              numericFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const numericValue = parseFloat(docData[field]);
                  docData[field] = isNaN(numericValue) ? null : numericValue;
                }
              });

              // HSGY is stored as a 4-digit String across the existing
              // TpStudents data (not a Number) — the Active/Archived Students
              // pages range-query against a String, so keep it a String here
              // too rather than coercing to a Number.
              if (
                docData.HSGY !== null &&
                docData.HSGY !== undefined &&
                docData.HSGY !== ""
              ) {
                docData.HSGY = String(docData.HSGY).trim();
              }

              // Handle boolean fields that use -1/0 values or true/false
              const studentBooleanFields = [
                "CCP_participant",
                "verified_SSID",
                "early_applicant_first_term",
                "sc_ctag_permission",
                "CTE_requirement",
                "MM3_ALG_II",
                "MM3_PreCalc",
                "scholarship",
              ];

              // Calculated fields that should not be imported from Excel
              const calculatedFields = ["senior_packet"];

              // Remove calculated fields from docData as they should be computed, not imported
              calculatedFields.forEach((field) => {
                if (docData.hasOwnProperty(field)) {
                  delete docData[field];
                  console.log(
                    `Removed calculated field '${field}' from import data`,
                  );
                }
              });

              studentBooleanFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const stringValue = docData[field]
                    .toString()
                    .toLowerCase()
                    .trim();
                  if (
                    stringValue === "yes" ||
                    stringValue === "true" ||
                    stringValue === "1" ||
                    stringValue === "-1"
                  ) {
                    docData[field] = -1;
                  } else if (
                    stringValue === "no" ||
                    stringValue === "false" ||
                    stringValue === "0"
                  ) {
                    docData[field] = 0;
                  } else {
                    docData[field] = 0; // Default to 0 for unrecognized values
                  }
                }
              });

              // Handle date fields specifically for students
              const studentDateFields = [
                "Application_Dt",
                "DOB",
                "Last_Updated",
                "added_to_portal",
              ];

              studentDateFields.forEach((field) => {
                if (
                  docData[field] !== null &&
                  docData[field] !== undefined &&
                  docData[field] !== ""
                ) {
                  const ts = formatDateInternal(docData[field]);
                  docData[field] = ts;
                  if (
                    ts === null &&
                    docData[field] !== null &&
                    docData[field] !== ""
                  ) {
                    console.warn(
                      `Date parsing failed for student field ${field} with value:`,
                      docData[field],
                    );
                  }
                }
              });

              // Ensure required fields have default values for new students
              if (!docData.Student_Guid || docData.Student_Guid === "") {
                // Generate a new GUID if not provided
                docData.Student_Guid = crypto.randomUUID();
              }

              // Set default application date if not provided
              if (!docData.Application_Dt) {
                docData.Application_Dt = formatDateInternal(new Date());
              }

              // Set default values for required fields
              if (!docData.CurrentEnrollmentStatus) {
                docData.CurrentEnrollmentStatus = "Active";
              }
            }

            // A blank Excel cell produces no key at all in the parsed row
            // (not an empty string), so a sparse/incomplete row — e.g. a
            // stray PivotTable summary row accidentally left in the sheet,
            // or a row where most columns weren't filled in — can otherwise
            // silently become a brand-new TpStudents document containing
            // nothing but the few defaults set above. Require a real name
            // before creating a new student record instead of inserting
            // that kind of garbage document. HSGY is checked separately and
            // just as strictly — the Active/Archived Students pages both
            // filter on it, so a student saved without it is invisible
            // everywhere in the app even though the document is real.
            const hasStudentName =
              (docData.FirstName && String(docData.FirstName).trim()) ||
              (docData.LastName && String(docData.LastName).trim());
            const hasStudentHSGY =
              docData.HSGY && String(docData.HSGY).trim();
            const studentRowError =
              collectionName !== "TpStudents"
                ? null
                : !hasStudentName
                  ? "Skipped — new student rows must include a First Name and Last Name."
                  : !hasStudentHSGY
                    ? "Skipped — new student rows must include HSGY (graduation year). Without it the student will not appear in the Active or Archived Students lists."
                    : null;
            const isInvalidNewStudentRow = Boolean(studentRowError);

            if (docId) {
              // For collections with a business logic primary key (like SiteID),
              // we need to find the existing document by querying the field value
              if (primaryKeyField !== "id") {
                // Query to find existing document with matching primary key field
                const existingQuery = query(
                  collection(db, collectionName),
                  where(primaryKeyField, "==", docId),
                );
                const querySnapshot = await getDocs(existingQuery);

                if (!querySnapshot.empty) {
                  // Update existing document
                  const existingDoc = querySnapshot.docs[0];
                  const existingData = existingDoc.data();
                  const docRef = doc(db, collectionName, existingDoc.id);

                  console.log(
                    `Existing document found for ${primaryKeyField} ${docId}:`,
                    existingData,
                  );
                  if (hasDataChanged(docData, existingData)) {
                    docData["updatedAt"] = Timestamp.now();
                    currentBatch.set(docRef, docData, { merge: true });
                    updateCount++;
                    console.log(
                      `Updating existing document with ${primaryKeyField} ${docId}`,
                    );
                  } else {
                    skippedCount++;
                    console.log(
                      `No changes detected for document with ${primaryKeyField} ${docId}, skipping`,
                    );
                    continue;
                  }
                } else if (isInvalidNewStudentRow) {
                  skippedCount++;
                  errorRows.push({
                    row: rowIndex + 2,
                    error: studentRowError,
                  });
                  continue;
                } else {
                  // Create new document with auto-generated Firestore ID
                  // If a business primary key was provided (e.g. SiteID), persist it on the new document
                  if (docId) {
                    docData[primaryKeyField] = docId;
                  }
                  docData["created_at"] = Timestamp.now();
                  docData["updatedAt"] = Timestamp.now();

                  // Special handling for CTAG collection - auto-populate dates for new records
                  if (collectionName === "TpCtagCatalog") {
                    const currentDate = new Date().toISOString().split("T")[0]; // Format as YYYY-MM-DD
                    if (!docData["Colleague_Start_Date"]) {
                      docData["Colleague_Start_Date"] = currentDate;
                    }
                    if (!docData["Colleague_End_Date"]) {
                      docData["Colleague_End_Date"] = currentDate;
                    }
                  }

                  const newDocRef = doc(collection(db, collectionName));
                  currentBatch.set(newDocRef, docData);
                  addCount++;
                  console.log(
                    `Adding new document with ${primaryKeyField} ${docId}, Firestore ID: ${newDocRef.id}`,
                  );
                }
              } else {
                // Traditional logic for collections that use Firestore document ID as primary key
                const docRef = doc(db, collectionName, docId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                  const existingData = docSnap.data();
                  console.log(
                    `Existing document found for ID ${docId}:`,
                    existingData,
                  );
                  if (hasDataChanged(docData, existingData)) {
                    docData["updatedAt"] = Timestamp.now();
                    currentBatch.set(docRef, docData, { merge: true });
                    updateCount++;
                    console.log(`Updating existing document with ID ${docId}`);
                  } else {
                    skippedCount++;
                    console.log(
                      `No changes detected for document with ID ${docId}, skipping`,
                    );
                    continue;
                  }
                } else {
                  docData["created_at"] = Timestamp.now();
                  docData["updatedAt"] = Timestamp.now();

                  // Special handling for CTAG collection - auto-populate dates for new records
                  if (collectionName === "TpCtagCatalog") {
                    const currentDate = new Date().toISOString().split("T")[0]; // Format as YYYY-MM-DD
                    if (!docData["Colleague_Start_Date"]) {
                      docData["Colleague_Start_Date"] = currentDate;
                    }
                    if (!docData["Colleague_End_Date"]) {
                      docData["Colleague_End_Date"] = currentDate;
                    }
                  }

                  currentBatch.set(docRef, docData);
                  addCount++;
                  console.log(`Adding new document with ID ${docId}`);
                }
              }
            } else if (isInvalidNewStudentRow) {
              skippedCount++;
              errorRows.push({
                row: rowIndex + 2,
                error: studentRowError,
              });
              continue;
            } else {
              docData["created_at"] = Timestamp.now();
              docData["updatedAt"] = Timestamp.now();

              // Special handling for CTAG collection - auto-populate dates for new records
              if (collectionName === "TpCtagCatalog") {
                const currentDate = new Date().toISOString().split("T")[0]; // Format as YYYY-MM-DD
                if (!docData["Colleague_Start_Date"]) {
                  docData["Colleague_Start_Date"] = currentDate;
                }
                if (!docData["Colleague_End_Date"]) {
                  docData["Colleague_End_Date"] = currentDate;
                }
              }

              const newDocRef = doc(collection(db, collectionName));
              currentBatch.set(newDocRef, docData);
              addCount++;
              console.log(
                `Adding new document without ID, auto-generated ID will be: ${newDocRef.id}`,
              );
            }

            operationCount++;

            if (operationCount >= 490) {
              await currentBatch.commit();
              currentBatch = writeBatch(db);
              operationCount = 0;
            }
          } catch (rowError) {
            errorRows.push({ row: rowIndex + 2, error: rowError.message });
            console.error(`Error processing row ${rowIndex + 2}:`, rowError);
          }
        }

        if (operationCount > 0) {
          try {
            await currentBatch.commit();
          } catch (batchError) {
            errorRows.push({ row: "final batch", error: batchError.message });
            console.error("Error committing final batch:", batchError);
          }
        }

        let summaryMsg = `Upload complete. Updated: ${updateCount}, Added: ${addCount}, Skipped (no changes): ${skippedCount}`;
        if (errorRows.length > 0) {
          summaryMsg += `. Errors: ${errorRows.length}. See console for details.`;
        }

        setSnackbar({
          open: true,
          message: summaryMsg,
          severity: errorRows.length > 0 ? "warning" : "success",
        });

        if (onActionComplete) {
          onActionComplete(errorRows.length === 0, summaryMsg);
        }
      } catch (error) {
        console.error("Error processing file:", error);
        setSnackbar({
          open: true,
          message: `Error processing file: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <Box p={inline ? 0 : 2}>
      <Button
        variant="contained"
        component="label"
        disabled={loading}
        startIcon={<FontAwesomeIcon icon={faUpload} />}
        sx={{ mr: 2 }}
        onClick={() => fileInputRef.current.click()}
      >
        Upload Data
      </Button>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
      <Button
        variant="contained"
        disabled={loading}
        startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
        sx={{ mr: 2 }}
        onClick={handleExport}
      >
        Export Data
      </Button>
      <Button
        variant="contained"
        color="success"
        disabled={loading}
        startIcon={<FontAwesomeIcon icon={faFileExcel} />}
        sx={{ color: "white" }}
        onClick={handleExportTemplate}
      >
        Export Template
      </Button>
      {loading && <CircularProgress size={24} sx={{ ml: 2 }} />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditUpload;
